import { useEffect, useRef, useState } from 'react'

import { getRentalPortalApplicantEmails } from '../../../../../../api'
import { EMPTY_LIST } from '../../../../../../constants/common'
import { useLocationPagination } from '../../../../../Pagination'
import {
  getIsMockApplicantId,
  getMockApplicantEmails,
} from '../../../../../RentalPortalApplicant'
import { APPLICANT_PROFILE_EMAILS_PAGE_SIZE } from '../../RentalPortalApplicantProfileEmails.constants'
import {
  getApplicantProfileEmailsReadKey,
  getIsApplicantProfileEmailsPageMoved,
  getIsApplicantProfileEmailsReadCurrent,
  getIsApplicantProfileEmailsReadPending,
} from '../../RentalPortalApplicantProfileEmails.utils'

export function useApplicantProfileEmails({
  applicantId,
  emailCount,
  isIdentityMasked,
}) {
  const { pagination: locationPagination, setPagination } =
    useLocationPagination({ isUrlBound: false })

  const { page } = locationPagination

  const [retryCount, setRetryCount] = useState(0)

  const [result, setResult] = useState(null)

  const readIdRef = useRef(0)

  const readKey = getApplicantProfileEmailsReadKey([
    applicantId,
    emailCount,
    isIdentityMasked,
    page,
    retryCount,
  ])

  function changePage(nextPage) {
    setPagination({ ...locationPagination, page: nextPage })
  }

  async function loadEmails() {
    readIdRef.current += 1

    const readId = readIdRef.current

    // TODO: drop the mock branch once the mock sections open backend applicants only
    const getEmails = getIsMockApplicantId(applicantId)
      ? getMockApplicantEmails
      : getRentalPortalApplicantEmails

    return getEmails({
      applicantId,
      page,
      size: APPLICANT_PROFILE_EMAILS_PAGE_SIZE,
    })
      .then(({ data }) => {
        if (getIsApplicantProfileEmailsReadCurrent({ readId, readIdRef })) {
          setResult({ data, hasError: false, key: readKey })

          if (getIsApplicantProfileEmailsPageMoved({ data, page })) {
            changePage(data.pagination.page)
          }
        }
      })
      .catch(() => {
        if (getIsApplicantProfileEmailsReadCurrent({ readId, readIdRef })) {
          setResult(prevResult => ({
            ...prevResult,
            hasError: true,
            key: readKey,
          }))
        }
      })
  }

  useEffect(() => {
    if (applicantId) {
      loadEmails()
    }

    return () => {
      readIdRef.current += 1
    }
  }, [applicantId, readKey])

  function handleSetPagination(nextPagination) {
    changePage(nextPagination.page)
  }

  function handleRefresh() {
    setRetryCount(prevCount => prevCount + 1)
  }

  return {
    handleRefresh,
    handleSetPagination,
    hasError: Boolean(result?.hasError),
    isInitiated: Boolean(result),
    isLoading: getIsApplicantProfileEmailsReadPending({ readKey, result }),
    items: result?.data?.items ?? EMPTY_LIST,
    pagination: {
      count: result?.data?.pagination?.total ?? 0,
      page,
      pageCount: result?.data?.pagination?.page_count ?? 0,
      pageSize: APPLICANT_PROFILE_EMAILS_PAGE_SIZE,
    },
  }
}
